import { useCallback, useEffect, useState } from "react";
import TimesheetManager from "../managers/TimesheetManager";
import {useSiteContext} from "../SiteContext";
import ActionButton from "./ActionButton";
import Colors from "../styles/Colors.json";
import {Day} from "../interfaces/EmployeeInterface";
import { getSuggestedSickRate, SuggestedSickRate } from "../api/timesheet";

interface TaskSetterProps {
    employeeSickClaimIDs: number[];
    initialAmount?: number;
    onUpdated?: () => void;
    /**
     * Comparison mode, used on payroll-approved timesheets: renders the saved rate next to
     * the computed 13-week lookback with the difference between them, so payroll can go back
     * and validate prior weeks. No input and no save - an approved rate is locked in and must
     * never be overwritten from here.
     */
    readOnly?: boolean;
}

const money = (value: number) =>
  `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const hours = (value: number) =>
  value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EXCLUSION_LABELS: Record<string, string> = {
  no_timesheet: "No timesheet",
  not_payroll_approved: "Not payroll approved",
  processing_error: "Could not be processed",
};

const TaskSetter: React.FC<TaskSetterProps> = ({
    employeeSickClaimIDs,
  initialAmount = 0,
  onUpdated,
  readOnly = false,
}) => {
  const [amount, setAmount] = useState(initialAmount.toString());
  const {setLoading, createPopup, employee, payPeriodID, setEmployee, timesheetID} = useSiteContext();

  // ── 13-week lookback suggestion (CA Labor Code 246(l)(3)) ───────────────────
  // Suggestion only: payroll still types/confirms a rate and saves it below. The API
  // returns method:'disabled' for clients where the feature is off, in which case this
  // panel looks exactly as it did before the feature existed.
  const [suggestion, setSuggestion] = useState<SuggestedSickRate | null>(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const parsedTimesheetID = parseInt(timesheetID!, 10);

  useEffect(() => {
    if (!parsedTimesheetID || isNaN(parsedTimesheetID) || employeeSickClaimIDs.length === 0) return;

    let cancelled = false;
    setSuggestionLoading(true);
    getSuggestedSickRate(parsedTimesheetID)
      .then((result) => { if (!cancelled) setSuggestion(result); })
      .finally(() => { if (!cancelled) setSuggestionLoading(false); });

    return () => { cancelled = true; };
  }, [parsedTimesheetID, employeeSickClaimIDs.length]);

  const closeBreakdown = useCallback(() => setBreakdownOpen(false), []);

  useEffect(() => {
    if (!breakdownOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeBreakdown(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [breakdownOpen, closeBreakdown]);

  // Only render once we have a rate worth offering. A zero suggestion (no history AND no
  // pay rate on file) is not actionable - the save endpoint rejects rates <= 0 anyway - so
  // stay out of the way and let payroll type the rate as they do today.
  const hasSuggestion =
    !!suggestion &&
    suggestion.success &&
    suggestion.method !== "disabled" &&
    (suggestion.suggestedRate ?? 0) > 0;

  const suggestedRate = suggestion?.suggestedRate ?? 0;
  const isFallback = suggestion?.method === "base_fallback";

  // On an approved timesheet we are comparing against payroll's manual figure, which has no
  // base-rate floor, so compare against the raw lookback where there is one.
  const comparisonRate = suggestion?.computedRate ?? suggestedRate;
  const deltaVsPayroll = Number((comparisonRate - initialAmount).toFixed(2));

  const applySuggestion = () => {
    setAmount(suggestedRate.toFixed(2));
    setBreakdownOpen(false);
  };

  const handleUpdate = async () => {
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount)) {
      alert("Please enter a valid number.");
      return;
    }

    setLoading(true);
    try {
      console.log(employeeSickClaimIDs);
      const result = await TimesheetManager.updateSickClaimAmount(employeeSickClaimIDs, parsedAmount);
      if (result?.success) {
        createPopup("Amount updated successfully.", "");
        onUpdated?.();
        const refreshedTimesheet = await TimesheetManager.refreshTimesheet(employee.employeeID, payPeriodID);
        employee.days = refreshedTimesheet;
        setEmployee(employee);

      } else {
        createPopup("Failed to update amount.", "");
      }
    } catch (error) {
      console.error("Error updating sick claim amount:", error);
      createPopup("An error occurred.", "");
    }
    setLoading(false);
  };

  const renderBreakdown = () => {
    if (!suggestion) return null;
    const periods = suggestion.periods ?? [];
    const totals = suggestion.totals;

    return (
      <div className="sick-bd-overlay" onClick={closeBreakdown}>
        <div className="sick-bd" onClick={(e) => e.stopPropagation()}>
          <div className="sick-bd-head">
            <span className="sick-bd-title">Sick Rate &mdash; 13-Week Lookback</span>
            <button className="sick-bd-x" onClick={closeBreakdown} aria-label="Close">&times;</button>
          </div>

          <div className="sick-bd-sub">
            Sick date {suggestion.anchorDate} &middot; the {suggestion.lookbackPeriods} complete weeks before
            this timesheet: {suggestion.windowStart} to {suggestion.windowEnd}
          </div>

          <div className="sick-bd-tablewrap">
            <table className="sick-bd-table">
              <thead>
                <tr>
                  <th>Week</th>
                  <th>Dates</th>
                  <th className="num">Hours</th>
                  <th className="num">Wages</th>
                </tr>
              </thead>
              <tbody>
                {periods.length === 0 && (
                  <tr><td colSpan={4} className="sick-bd-empty">No complete pay periods in this window.</td></tr>
                )}
                {periods.map((p) => (
                  <tr key={p.payPeriodID} className={p.included ? "" : "excluded"}>
                    <td>{p.weekNumber}</td>
                    <td>{p.startDate} &ndash; {p.endDate}</td>
                    {p.included ? (
                      <>
                        <td className="num">{hours(p.hoursWorked)}</td>
                        <td className="num">{money(p.wages)}</td>
                      </>
                    ) : (
                      <td className="num reason" colSpan={2}>
                        {EXCLUSION_LABELS[p.excludedReason ?? ""] ?? "Excluded"}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot>
                  <tr>
                    <td colSpan={2}>
                      Total &mdash; {totals.periodsIncluded} of {totals.periodsIncluded + totals.periodsExcluded} weeks used
                    </td>
                    <td className="num">{hours(totals.hoursWorked)}</td>
                    <td className="num">{money(totals.wages)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {suggestion.method === "lookback" && totals ? (
            <div className="sick-bd-formula">
              {money(totals.wages)} &divide; {hours(totals.hoursWorked)} hrs ={" "}
              <strong>{money(suggestion.computedRate ?? 0)}/hr</strong>
            </div>
          ) : (
            <div className="sick-bd-formula warn">
              {suggestion.insufficientHistoryReason ?? "Not enough history to compute a lookback rate."}
            </div>
          )}

          <div className="sick-bd-rates">
            <div>
              <span>Base hourly rate</span>
              <strong>{suggestion.baseRate !== null && suggestion.baseRate !== undefined ? money(suggestion.baseRate) : "n/a"}</strong>
            </div>
            {readOnly && initialAmount > 0 && (
              <div>
                <span>Rate entered by payroll</span>
                <strong>{money(initialAmount)}</strong>
              </div>
            )}
            <div className="highlight">
              <span>Suggested rate {suggestion.suggestedSource === "base" ? "(base rate is higher)" : "(13-week lookback)"}</span>
              <strong>{money(suggestedRate)}</strong>
            </div>
          </div>

          <div className="sick-bd-note">
            Wages are total pre-tax pay for each week &mdash; including overtime and double-time
            premium, incentive and rest premium &mdash; less prior sick pay and less fringe
            (prevailing wage, union and catch-up). Hours are actual hours worked, so sick and PTO
            are out of both sides. Phone reimbursement is not included. Only payroll-approved
            weeks are counted.
          </div>

          <div className="sick-bd-actions">
            <button className="sick-bd-btn" onClick={closeBreakdown}>Close</button>
            {!readOnly && (
              <button className="sick-bd-btn primary" onClick={applySuggestion}>
                Apply {money(suggestedRate)}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="task-setter-container">
      <div className="sick-header">
        <span>Sick Claim Hourly Rate</span>
      </div>
      <div className="sick-amount-setter">
        {readOnly ? (
          // Approved timesheet: a comparison view. Show the rate payroll saved so it can be
          // read against the computed one below. Nothing is editable - the rate is locked in.
          <div className="sick-saved">
            <span className="sick-saved-label">Rate entered by payroll</span>
            <span className="sick-saved-value">
              {initialAmount > 0 ? `${money(initialAmount)}/hr` : "None entered"}
            </span>
          </div>
        ) : (
          <label>
            Hourly Rate: $
            <input
              type="text"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={employeeSickClaimIDs.length === 0}
            />
          </label>
        )}

        {suggestionLoading && (
          <div className="sick-suggest loading">
            {readOnly ? "Computing rate…" : "Computing suggested rate…"}
          </div>
        )}

        {!suggestionLoading && hasSuggestion && (
          <div className={`sick-suggest${isFallback ? " fallback" : ""}`}>
            {readOnly ? (
              // Comparing against what payroll already entered, so lead with the raw
              // lookback figure - payroll's manual calc has no base-rate floor either.
              <>
                <div className="sick-suggest-rate">
                  Computed: <strong>{money(comparisonRate)}/hr</strong>
                </div>
                <div className="sick-suggest-note">
                  {isFallback ? "Base rate — insufficient history" : "13-week lookback"}
                  {!isFallback && suggestion?.suggestedSource === "base" &&
                    ` · base rate ${money(suggestion?.baseRate ?? 0)} would apply`}
                </div>
                {initialAmount > 0 && (
                  <div className={`sick-suggest-delta${deltaVsPayroll === 0 ? " match" : ""}`}>
                    {deltaVsPayroll === 0
                      ? "Matches the rate payroll entered"
                      : `${deltaVsPayroll > 0 ? "+" : "−"}${money(Math.abs(deltaVsPayroll))} vs the rate payroll entered`}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="sick-suggest-rate">
                  Suggested: <strong>{money(suggestedRate)}/hr</strong>
                </div>
                <div className="sick-suggest-note">
                  {isFallback
                    ? "Base rate — insufficient history"
                    : suggestion?.suggestedSource === "base"
                      ? `Base rate — 13-week lookback computed ${money(suggestion?.computedRate ?? 0)}`
                      : "13-week lookback"}
                </div>
              </>
            )}
            <div className="sick-suggest-actions">
              {!readOnly && (
                <button type="button" className="sick-suggest-apply" onClick={applySuggestion}>Apply</button>
              )}
              <button type="button" className="sick-suggest-link" onClick={() => setBreakdownOpen(true)}>
                View breakdown
              </button>
            </div>
          </div>
        )}

        {!readOnly && (
          <ActionButton
            title="Set Amount"
            onAction={handleUpdate}
            isEnabled={amount !== "" && amount !== "0"}
          />
        )}

        {readOnly && (
          <div className="sick-readonly-note">
            Already payroll approved &mdash; shown for comparison only, the saved rate is unchanged.
          </div>
        )}
      </div>

      {breakdownOpen && renderBreakdown()}
    </div>
  );
};

export default TaskSetter;

// Add CSS styles directly in this file using a template literal
const styles = `
/* TaskSetter.css */

.sick-header {
  background-color: #27C2F1; /* You can also use Colors.Sick if injected dynamically */
  color: white;
  display: flex;
  justify-content: center;
  padding: 0.5rem;
  font-weight: bold;
  font-size: 0.9rem;
  font-family: sans-serif;
}

.task-setter-container {
  display: flex;
  flex-direction: column;
}

.sick-amount-setter {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-radius: 0px;
  background-color: #f8d7da;
  padding: 1rem;
  border: 1px solid #ccc;
  max-width: 300px;
  font-family: sans-serif;
}

.sick-amount-setter label {
  font-weight: 600;
  color: #333;
  display: flex;
  flex-direction: column;
  font-size: 0.9rem;
}

.sick-amount-setter input {
  margin-top: 0.25rem;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #aaa;
  font-size: 1rem;
  background-color: #fff;
  transition: border 0.2s;
}

.sick-amount-setter input:disabled {
  background-color: #e9ecef;
  border-color: #ddd;
  color: #666;
}

/* Read-only saved rate (payroll-approved timesheets) */
.sick-saved {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.sick-saved-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 700;
  color: #7a6a6c;
}

.sick-saved-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #333;
}

.sick-readonly-note {
  font-size: 0.72rem;
  font-style: italic;
  color: #7a6a6c;
  line-height: 1.35;
}

/* ── 13-week lookback suggestion ─────────────────────────────────────────── */

.sick-suggest {
  background-color: #fff;
  border: 1px solid #d7dce3;
  border-left: 3px solid #276EF1;
  border-radius: 4px;
  padding: 0.5rem 0.6rem;
  font-size: 0.8rem;
  color: #333;
}

.sick-suggest.loading {
  border-left-color: #b9c0ca;
  color: #666;
  font-style: italic;
}

.sick-suggest.fallback {
  border-left-color: #E8A33D;
  background-color: #fffaf1;
}

.sick-suggest-rate {
  font-size: 0.85rem;
}

.sick-suggest-rate strong {
  font-size: 0.95rem;
}

.sick-suggest-note {
  color: #6b7280;
  font-size: 0.72rem;
  margin-top: 0.1rem;
}

.sick-suggest-delta {
  margin-top: 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #b45309;
}

.sick-suggest-delta.match {
  color: #16794c;
}

.sick-suggest-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.45rem;
}

.sick-suggest-apply {
  background-color: #276EF1;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.7rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.sick-suggest-apply:hover { background-color: #1c5ad0; }

.sick-suggest-link {
  background: none;
  border: none;
  padding: 0;
  color: #276EF1;
  font-size: 0.78rem;
  text-decoration: underline;
  cursor: pointer;
  font-family: inherit;
}

/* ── Breakdown modal ────────────────────────────────────────────────────── */

.sick-bd-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  font-family: sans-serif;
}

.sick-bd {
  background: #fff;
  border-radius: 8px;
  width: 560px;
  max-width: 92vw;
  max-height: 88vh;
  overflow-y: auto;
  padding: 1.1rem 1.25rem 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  text-align: left;
  color: #333;
}

.sick-bd-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sick-bd-title {
  font-size: 1rem;
  font-weight: 700;
}

.sick-bd-x {
  background: none;
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  color: #888;
  cursor: pointer;
  padding: 0 0.2rem;
}

.sick-bd-x:hover { color: #333; }

.sick-bd-sub {
  font-size: 0.78rem;
  color: #6b7280;
  margin-top: 0.15rem;
  margin-bottom: 0.75rem;
}

.sick-bd-tablewrap {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 5px;
}

.sick-bd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.sick-bd-table th,
.sick-bd-table td {
  padding: 0.35rem 0.6rem;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
}

.sick-bd-table thead th {
  background: #f7f8fa;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
  text-align: left;
}

.sick-bd-table .num { text-align: right; }

.sick-bd-table tbody tr.excluded td {
  color: #9ca3af;
  background: #fbfbfc;
}

.sick-bd-table td.reason {
  font-style: italic;
  font-size: 0.75rem;
}

.sick-bd-empty {
  text-align: center;
  color: #9ca3af;
  font-style: italic;
  padding: 0.75rem;
}

.sick-bd-table tfoot td {
  background: #f7f8fa;
  font-weight: 700;
  border-top: 1px solid #e5e7eb;
  border-bottom: none;
}

.sick-bd-formula {
  margin-top: 0.75rem;
  padding: 0.5rem 0.7rem;
  background: #eef4ff;
  border-radius: 5px;
  font-size: 0.9rem;
  text-align: center;
}

.sick-bd-formula.warn {
  background: #fff5e6;
  font-size: 0.82rem;
}

.sick-bd-rates {
  margin-top: 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.82rem;
}

.sick-bd-rates > div {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.3rem 0.1rem;
  border-bottom: 1px solid #f0f0f0;
}

.sick-bd-rates > div.highlight {
  border-bottom: none;
  font-size: 0.9rem;
}

.sick-bd-rates > div.highlight strong { color: #276EF1; }

.sick-bd-note {
  margin-top: 0.6rem;
  font-size: 0.72rem;
  color: #6b7280;
  line-height: 1.4;
}

.sick-bd-actions {
  margin-top: 0.9rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.sick-bd-btn {
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 0.35rem 0.9rem;
  font-size: 0.82rem;
  cursor: pointer;
  font-family: inherit;
}

.sick-bd-btn:hover { background: #f5f5f5; }

.sick-bd-btn.primary {
  background: #276EF1;
  border-color: #276EF1;
  color: #fff;
  font-weight: 600;
}

.sick-bd-btn.primary:hover { background: #1c5ad0; }
`;

const styleTag = document.createElement('style');
styleTag.innerHTML = styles;
document.head.appendChild(styleTag);
